import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — The Intuitive Idea of a Limit
// ─────────────────────────────────────────────────────────────────
function DiagramLimitIdea() {
  const W = 900, H = 340;

  // f(x) = (x²-1)/(x-1) = x+1  with a hole at x=1
  // Left panel: numeric approach from both sides
  // Right panel: graph showing the hole

  const f = x => (x * x - 1) / (x - 1); // = x+1, undefined at x=1

  const ox = 510, oy = 240, scX = 130, scY = 65;
  const toX = x => ox + (x - 1) * scX;
  const toY = y => oy - (y - 2) * scY;

  // curve points, skipping x=1
  const leftPts = [], rightPts = [];
  for (let x = -0.5; x < 0.993; x += 0.03) {
    leftPts.push(`${toX(x).toFixed(1)},${toY(f(x)).toFixed(1)}`);
  }
  for (let x = 1.007; x <= 2.5; x += 0.03) {
    rightPts.push(`${toX(x).toFixed(1)},${toY(f(x)).toFixed(1)}`);
  }

  // Left panel: approaching-values table
  const leftVals  = [0.5, 0.8, 0.9, 0.99, 0.999];
  const rightVals = [1.5, 1.2, 1.1, 1.01, 1.001];
  const panW = 440;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Intuitive Idea of a Limit — f(x) = (x²−1)/(x−1) as x → 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: numeric table panel ── */}
        <rect x={10} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panW/2+10} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Values of f(x) approaching x = 1 from both sides</text>

        {/* Table header */}
        <rect x={22} y={42} width={200} height={24} rx={3} fill="#2E75B6"/>
        <rect x={232} y={42} width={200} height={24} rx={3} fill="#c0392b"/>
        <text x={122} y={58} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">x → 1⁻  (from left)</text>
        <text x={332} y={58} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">x → 1⁺  (from right)</text>

        {/* Column sub-headers */}
        {['x', 'f(x)', 'x', 'f(x)'].map((h, i) => (
          <text key={i} x={[50, 172, 260, 392][i]} y={82} textAnchor="middle"
            fontSize="11" fill="#888" fontWeight="700">{h}</text>
        ))}
        <line x1={22} y1={86} x2={432} y2={86} stroke="#eee" strokeWidth="1"/>

        {leftVals.map((x, i) => {
          const fx = f(x).toFixed(4);
          const rx = rightVals[i], rfx = f(rx).toFixed(4);
          const rowY = 100 + i * 28;
          const isClose = i >= 3;
          return (
            <g key={i}>
              <rect x={22} y={rowY-14} width={410} height={26} fill={i%2===0 ? '#f8fbff' : '#fff'}/>
              <text x={50}  y={rowY+2} textAnchor="middle" fontSize="12" fill={isClose ? '#2E75B6' : '#555'}
                fontFamily="'Courier New',monospace" fontWeight={isClose?'700':'400'}>{x}</text>
              <text x={172} y={rowY+2} textAnchor="middle" fontSize="12" fill={isClose ? '#2E75B6' : '#555'}
                fontFamily="'Courier New',monospace" fontWeight={isClose?'700':'400'}>{fx}</text>
              <text x={260} y={rowY+2} textAnchor="middle" fontSize="12" fill={isClose ? '#c0392b' : '#555'}
                fontFamily="'Courier New',monospace" fontWeight={isClose?'700':'400'}>{rx}</text>
              <text x={392} y={rowY+2} textAnchor="middle" fontSize="12" fill={isClose ? '#c0392b' : '#555'}
                fontFamily="'Courier New',monospace" fontWeight={isClose?'700':'400'}>{rfx}</text>
            </g>
          );
        })}

        {/* Convergence arrows */}
        <line x1={22} y1={246} x2={432} y2={246} stroke="#eee" strokeWidth="1.5"/>
        <rect x={22} y={252} width={410} height={36} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={237} y={268} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">Both sides approach 2</text>
        <text x={237} y={282} textAnchor="middle" fontSize="13" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">lim f(x) = 2,  even though f(1) is undefined</text>

        {/* Key insight */}
        <rect x={22} y={294} width={410} height={28} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={237} y={312} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">A limit describes behaviour near x = a, not the value at x = a</text>

        {/* ── RIGHT: graph ── */}
        <rect x={460} y={10} width={W-474} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={(460+W-14)/2} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Graph of f(x) = x+1 with hole at (1, 2)</text>

        {/* Grid */}
        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={toX(v < 0 ? -0.5 : v)} y1={toY(-0.2)} x2={toX(v < 0 ? -0.5 : v)} y2={toY(3.8)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={toX(-0.5)} y1={toY(v+1)} x2={toX(2.5)} y2={toY(v+1)} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}

        {/* Axes */}
        <line x1={toX(-0.5)} y1={oy} x2={toX(2.6)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={toY(3.8)} x2={ox} y2={toY(-0.3)} stroke="#1F3864" strokeWidth="1.5"/>
        <polygon points={`${toX(2.6)},${oy} ${toX(2.6)-8},${oy-4} ${toX(2.6)-8},${oy+4}`} fill="#1F3864"/>
        <polygon points={`${ox},${toY(3.8)} ${ox-4},${toY(3.8)+8} ${ox+4},${toY(3.8)+8}`} fill="#1F3864"/>
        <text x={toX(2.6)+6} y={oy+5} fontSize="14" fill="#1F3864" fontWeight="700">x</text>
        <text x={ox+5} y={toY(3.8)-4} fontSize="14" fill="#1F3864" fontWeight="700">y</text>

        {/* Tick labels */}
        {[-0,1,2].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy-4} x2={toX(v)} y2={oy+4} stroke="#888" strokeWidth="1.2"/>
            <text x={toX(v)} y={oy+17} textAnchor="middle" fontSize="12" fill="#aaa">{v}</text>
          </g>
        ))}
        {[1,2,3].map(v => (
          <g key={v}>
            <line x1={ox-4} y1={toY(v)} x2={ox+4} y2={toY(v)} stroke="#888" strokeWidth="1.2"/>
            <text x={ox-8} y={toY(v)+4} textAnchor="end" fontSize="12" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Curve segments */}
        <polyline points={leftPts.join(' ')}  fill="none" stroke="#2E75B6" strokeWidth="3"/>
        <polyline points={rightPts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="3"/>

        {/* Hole at (1,2) */}
        <circle cx={toX(1)} cy={toY(2)} r={7} fill="#fff" stroke="#2E75B6" strokeWidth="2.5"/>

        {/* Limit arrows from both sides */}
        <line x1={toX(0.5)} y1={toY(1.5)} x2={toX(0.92)} y2={toY(1.92)} stroke="#2E75B6" strokeWidth="2"
          markerEnd="url(#arrB)" strokeDasharray="5,3"/>
        <line x1={toX(1.5)} y1={toY(2.5)} x2={toX(1.08)} y2={toY(2.08)} stroke="#c0392b" strokeWidth="2"
          markerEnd="url(#arrR)" strokeDasharray="5,3"/>

        <defs>
          <marker id="arrB" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/>
          </marker>
          <marker id="arrR" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <polygon points="0 0,7 3.5,0 7" fill="#c0392b"/>
          </marker>
        </defs>

        <text x={toX(0.42)} y={toY(1.35)} fontSize="12" fill="#2E75B6" fontWeight="700">x→1⁻</text>
        <text x={toX(1.52)} y={toY(2.62)} fontSize="12" fill="#c0392b" fontWeight="700">x→1⁺</text>

        {/* Limit label */}
        <text x={toX(1)+10} y={toY(2)-16} fontSize="12" fill="#27ae60" fontWeight="700">limit = 2</text>
        <text x={toX(1)+10} y={toY(2)-2}  fontSize="11" fill="#27ae60">(hole — not defined)</text>
      </svg>
      <div style={S.dCaption}>
        The limit of f(x) as x→1 equals 2, even though f(1) is undefined (the expression 0/0). Numerically, values from both sides converge to 2. Graphically, the curve is the straight line y = x+1 with a single point punched out. The limit is about the journey, not the destination.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — One-Sided Limits and Limit Laws
// ─────────────────────────────────────────────────────────────────
function DiagramOneSidedAndLaws() {
  const W = 900, H = 340;

  // Left: piecewise function showing one-sided limits disagreeing
  // f(x) = { x+1, x<2;  3, x=2;  x-0.5, x>2 }
  const fL = x => x + 1;     // x < 2 → approaches 3
  const fR = x => x - 0.5;   // x > 2 → approaches 1.5
  const fAt = 3;              // f(2) = 3

  const ox1 = 65, oy1 = 220, sc1 = 68;
  const tX1 = x => ox1 + x * sc1;
  const tY1 = y => oy1 - y * sc1;

  const leftPts1 = [], rightPts1 = [];
  for (let x = -0.2; x < 1.97; x += 0.04) leftPts1.push(`${tX1(x).toFixed(1)},${tY1(fL(x)).toFixed(1)}`);
  for (let x = 2.03; x <= 3.8;  x += 0.04) rightPts1.push(`${tX1(x).toFixed(1)},${tY1(fR(x)).toFixed(1)}`);

  // Right panel: limit laws table
  const panX = 450, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>One-Sided Limits, Non-Existence, and the Limit Laws</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: graph ── */}
        <rect x={10} y={10} width={430} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={225} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">When one-sided limits differ — the two-sided limit does not exist</text>

        {/* Grid */}
        {[0,1,2,3,4].map(v => (
          <line key={v} x1={tX1(v)} y1={tY1(-0.2)} x2={tX1(v)} y2={tY1(3.8)} stroke="#f0f0f0" strokeWidth="1"/>
        ))}
        {[0,1,2,3].map(v => (
          <line key={v} x1={tX1(-0.2)} y1={tY1(v)} x2={tX1(3.8)} y2={tY1(v)} stroke="#f0f0f0" strokeWidth="1"/>
        ))}

        {/* Axes */}
        <line x1={tX1(-0.2)} y1={oy1} x2={tX1(3.9)} y2={oy1} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox1}       y1={tY1(3.8)} x2={ox1} y2={tY1(-0.2)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={tX1(3.9)+4} y={oy1+5} fontSize="13" fill="#1F3864">x</text>
        <text x={ox1+4} y={tY1(3.8)-4} fontSize="13" fill="#1F3864">y</text>

        {[1,2,3].map(v => (
          <g key={v}>
            <line x1={tX1(v)} y1={oy1-4} x2={tX1(v)} y2={oy1+4} stroke="#888" strokeWidth="1.2"/>
            <text x={tX1(v)} y={oy1+17} textAnchor="middle" fontSize="12" fill="#aaa">{v}</text>
          </g>
        ))}
        {[1,2,3].map(v => (
          <g key={v}>
            <line x1={ox1-4} y1={tY1(v)} x2={ox1+4} y2={tY1(v)} stroke="#888" strokeWidth="1.2"/>
            <text x={ox1-8} y={tY1(v)+4} textAnchor="end" fontSize="12" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Left branch (approaches 3 from left) */}
        <polyline points={leftPts1.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2.8"/>
        {/* open circle at (2, fL(2)=3) */}
        <circle cx={tX1(2)} cy={tY1(3)} r={6} fill="#fff" stroke="#2E75B6" strokeWidth="2.2"/>

        {/* Right branch (approaches 1.5 from right) */}
        <polyline points={rightPts1.join(' ')} fill="none" stroke="#c0392b" strokeWidth="2.8"/>
        {/* open circle at (2, fR(2)=1.5) */}
        <circle cx={tX1(2)} cy={tY1(1.5)} r={6} fill="#fff" stroke="#c0392b" strokeWidth="2.2"/>

        {/* Filled dot at (2, 3) = actual value */}
        <circle cx={tX1(2)} cy={tY1(fAt)} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
        <text x={tX1(2)+10} y={tY1(3)+4} fontSize="11" fill="#27ae60" fontWeight="700">f(2) = 3  (defined value)</text>

        {/* Annotations */}
        <text x={tX1(1.35)} y={tY1(fL(1.35))-14} fontSize="12" fill="#2E75B6" fontWeight="700">lim⁻ = 3</text>
        <text x={tX1(2.6)}  y={tY1(fR(2.6))+18}  fontSize="12" fill="#c0392b" fontWeight="700">lim⁺ = 1.5</text>

        {/* Jump annotation */}
        <line x1={tX1(2)+14} y1={tY1(3)} x2={tX1(2)+14} y2={tY1(1.5)} stroke="#e07b00" strokeWidth="2.2"/>
        <line x1={tX1(2)+9}  y1={tY1(3)}   x2={tX1(2)+19} y2={tY1(3)}   stroke="#e07b00" strokeWidth="1.5"/>
        <line x1={tX1(2)+9}  y1={tY1(1.5)} x2={tX1(2)+19} y2={tY1(1.5)} stroke="#e07b00" strokeWidth="1.5"/>
        <text x={tX1(2)+22} y={(tY1(3)+tY1(1.5))/2+4} fontSize="11" fill="#e07b00" fontWeight="700">jump</text>

        {/* Verdict box */}
        <rect x={20} y={H-62} width={410} height={44} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={225} y={H-45} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">lim⁻ f(x) ≠ lim⁺ f(x)  →  two-sided limit does NOT exist</text>
        <text x={225} y={H-28} textAnchor="middle" fontSize="11" fill="#555">Note: f(2) = 3 is defined, but it plays no role in the limit</text>

        {/* ── RIGHT: limit laws ── */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">The Limit Laws</text>
        <text x={panX+panW/2} y={43} textAnchor="middle" fontSize="11" fill="#888">If lim f = L and lim g = M (both exist), then:</text>

        {[
          { law:'Sum / Difference', formula:'lim [f ± g] = L ± M',    color:'#2E75B6' },
          { law:'Constant Multiple', formula:'lim [c·f] = c·L',        color:'#2E75B6' },
          { law:'Product',          formula:'lim [f·g] = L·M',         color:'#27ae60' },
          { law:'Quotient',         formula:'lim [f/g] = L/M  (M ≠ 0)',color:'#27ae60' },
          { law:'Power',            formula:'lim [fⁿ] = Lⁿ',           color:'#e07b00' },
          { law:'Root',             formula:'lim [ⁿ√f] = ⁿ√L  (L ≥ 0)',color:'#e07b00' },
          { law:'Composition',      formula:'lim f(g(x)) = f(M)\n(if f continuous at M)',color:'#9b59b6' },
        ].map(({ law, formula, color }, i) => {
          const ry = 54 + i * 38;
          return (
            <g key={law}>
              <rect x={panX+8} y={ry} width={panW-16} height={35} rx={3}
                fill={i%2===0 ? '#f8fbff' : '#fff'} stroke="#eee" strokeWidth="0.5"/>
              <rect x={panX+8} y={ry} width={4} height={35} fill={color} rx={1}/>
              <text x={panX+20} y={ry+14} fontSize="11" fill="#1F3864" fontWeight="700">{law}</text>
              {formula.split('\n').map((ln, j) => (
                <text key={j} x={panX+20} y={ry+14+14+j*13} fontSize="11" fill={color}
                  fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}

        {/* Direct substitution box */}
        <rect x={panX+8} y={H-68} width={panW-16} height={52} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={panX+panW/2} y={H-53} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Direct Substitution (polynomials, rationals at non-zeros)</text>
        <text x={panX+panW/2} y={H-37} textAnchor="middle" fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">lim[x→a] p(x) = p(a)  — just plug in x = a</text>
        <text x={panX+panW/2} y={H-22} textAnchor="middle" fontSize="10" fill="#555">Works whenever f is continuous at a</text>
      </svg>
      <div style={S.dCaption}>
        A two-sided limit exists only when both one-sided limits agree. Here the left branch approaches 3 and the right branch approaches 1.5, so no limit exists at x=2 — even though f(2)=3 is well-defined. The limit laws let you evaluate limits of combinations by breaking them into simpler pieces, as long as each piece has a limit.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Indeterminate Forms and Techniques
// ─────────────────────────────────────────────────────────────────
function DiagramIndeterminateForms() {
  const W = 900, H = 360;

  const forms = [
    {
      form: '0/0',
      color: '#2E75B6', fill: '#EBF3FB',
      title: 'Factor & Cancel',
      when: 'Polynomial/rational\nexpressions',
      steps: [
        'Factor numerator & denominator',
        'Cancel the common (x−a) factor',
        'Substitute x = a into result',
      ],
      ex: 'lim (x²−4)/(x−2)  as x→2',
      sol: '= lim (x+2)(x−2)/(x−2)\n= lim (x+2) = 4',
    },
    {
      form: '0/0',
      color: '#c0392b', fill: '#fff0f0',
      title: 'Multiply by Conjugate',
      when: 'Square roots in\nnumerator or denominator',
      steps: [
        'Multiply top & bottom by conjugate',
        'Use (a−b)(a+b) = a²−b² to simplify',
        'Cancel, then substitute',
      ],
      ex: 'lim (√x − 2)/(x−4)  as x→4',
      sol: '× (√x+2)/(√x+2):\n= lim (x−4)/[(x−4)(√x+2)]\n= 1/(2+2) = 1/4',
    },
    {
      form: '0/0',
      color: '#27ae60', fill: '#f0fff4',
      title: "L'Hôpital's Rule",
      when: '0/0 or ∞/∞ — and\nderivatives are simpler',
      steps: [
        'Verify 0/0 or ∞/∞ form',
        'Differentiate numerator and denominator separately',
        'Evaluate — repeat if still indeterminate',
      ],
      ex: 'lim sin(x)/x  as x→0',
      sol: '0/0 form; differentiate:\n= lim cos(x)/1\n= cos(0) = 1',
    },
    {
      form: '∞/∞',
      color: '#9b59b6', fill: '#f5d0ff',
      title: 'Divide by Highest Power',
      when: 'Rational function\nas x → ±∞',
      steps: [
        'Identify highest power of x in denominator',
        'Divide every term top & bottom by it',
        '1/xⁿ → 0, leaving only leading coefficients',
      ],
      ex: 'lim (3x²+1)/(2x²−5)  as x→∞',
      sol: 'Divide by x²:\n= lim (3+1/x²)/(2−5/x²)\n= 3/2',
    },
    {
      form: '∞−∞, 0·∞',
      color: '#e07b00', fill: '#fff7ee',
      title: 'Rewrite & Combine',
      when: 'Differences of large\nquantities; 0·∞ products',
      steps: [
        'Rewrite 0·∞ as 0/(1/∞) or ∞/(1/0)',
        'Combine fractions to get 0/0 or ∞/∞',
        "Apply L'Hôpital or another technique",
      ],
      ex: 'lim x·ln(x)  as x→0⁺',
      sol: '= lim ln(x)/(1/x)  →  −∞/∞\nL\'Hôp: (1/x)/(−1/x²) = −x → 0',
    },
  ];

  const panW = (W - 20) / forms.length - 5;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Indeterminate Forms and Techniques — 0/0, ∞/∞, 0·∞, ∞−∞</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {forms.map(({ form, color, fill, title, when, steps, ex, sol }, i) => {
          const px = 10 + i * (panW + 6);
          return (
            <g key={i}>
              <rect x={px} y={10} width={panW} height={H-20} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              {/* Form badge + header */}
              <rect x={px+4} y={14} width={panW-8} height={30} rx={3} fill={color}/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="10" fill="#fff" fontFamily="'Courier New',monospace" fontWeight="700">form: {form}</text>
              <text x={px+panW/2} y={40} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>

              {/* When to use */}
              <rect x={px+6} y={50} width={panW-12} height={32} rx={2} fill="#fff" stroke={color} strokeWidth="1"/>
              {when.split('\n').map((ln, j) => (
                <text key={j} x={px+panW/2} y={63+j*13} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{ln}</text>
              ))}

              {/* Steps */}
              <text x={px+8} y={100} fontSize="10" fill="#1F3864" fontWeight="700">Steps:</text>
              {steps.map((s, j) => (
                <g key={j}>
                  <circle cx={px+16} cy={113+j*20-4} r={7} fill={color}/>
                  <text x={px+16} y={113+j*20-0} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">{j+1}</text>
                  <text x={px+28} y={113+j*20} fontSize="10" fill="#333">{s}</text>
                </g>
              ))}

              {/* Example */}
              <line x1={px+8} y1={180} x2={px+panW-8} y2={180} stroke="#ddd" strokeWidth="1"/>
              <text x={px+8} y={194} fontSize="10" fill="#1F3864" fontWeight="700">Example:</text>
              <text x={px+8} y={208} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{ex}</text>
              <line x1={px+8} y1={214} x2={px+panW-8} y2={214} stroke={color} strokeWidth="0.8" strokeDasharray="3,2"/>
              {sol.split('\n').map((ln, j) => (
                <text key={j} x={px+8} y={228+j*15} fontSize="10" fill={color} fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        An indeterminate form is not a number — it signals that the limit requires more work. Factor-and-cancel and conjugate multiplication directly remove the problem. L'Hôpital's rule replaces the ratio with its derivative ratio (only valid for 0/0 or ∞/∞). For rational functions at infinity, dividing by the highest power reduces the problem to simple arithmetic.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Continuity: Definition, Types of Discontinuity
// ─────────────────────────────────────────────────────────────────
function DiagramContinuity() {
  const W = 900, H = 330;

  const panW = (W - 28) / 4 - 5;

  // Helper axes
  const ax = (px, oy, sc, H2) => ({
    tX: x => px + 28 + x * sc,
    tY: y => oy - y * sc,
    draw: (
      <g>
        <line x1={px+18} y1={oy} x2={px+panW-10} y2={oy} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={px+28} y1={oy+20} x2={px+28} y2={oy-H2} stroke="#ccc" strokeWidth="1.2"/>
      </g>
    ),
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Continuity — The Three Conditions and the Four Types of Discontinuity</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Top definition banner */}
        <rect x={14} y={10} width={W-28} height={40} rx={5} fill="#1F3864"/>
        <text x={W/2} y={26} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">f is continuous at x = a  iff all three conditions hold simultaneously:</text>
        <text x={W/2} y={43} textAnchor="middle" fontSize="12" fill="#aac8e8">
          {'(1) f(a) is defined   (2) lim[x→a] f(x) exists   (3) lim[x→a] f(x) = f(a)'}
        </text>

        {/* Four discontinuity panels */}
        {[
          {
            title: 'Removable',
            subtitle: '(hole)',
            color: '#2E75B6', fill: '#EBF3FB',
            desc: 'Limit exists, but f(a)\nis undefined or wrong value.\nFix: redefine f(a) = lim.',
            draw: ({ tX, tY }) => (
              <g>
                {/* Straight line with hole */}
                <line x1={tX(-1)} y1={tY(-0.5)} x2={tX(0.92)} y2={tY(0.46)} stroke="#2E75B6" strokeWidth="2.5"/>
                <line x1={tX(1.08)} y1={tY(0.54)} x2={tX(2)} y2={tY(1)} stroke="#2E75B6" strokeWidth="2.5"/>
                <circle cx={tX(1)} cy={tY(0.5)} r={6} fill="#fff" stroke="#2E75B6" strokeWidth="2"/>
                {/* Wrong value dot */}
                <circle cx={tX(1)} cy={tY(1.4)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
                <text x={tX(1)+8} y={tY(1.4)+4} fontSize="9" fill="#c0392b">f(a)</text>
              </g>
            ),
          },
          {
            title: 'Jump',
            subtitle: '(step)',
            color: '#c0392b', fill: '#fff0f0',
            desc: 'Left and right limits exist\nbut are unequal.\nCannot be fixed by redefining.',
            draw: ({ tX, tY }) => (
              <g>
                <line x1={tX(-1)} y1={tY(0.5)} x2={tX(0.93)} y2={tY(0.5)} stroke="#c0392b" strokeWidth="2.5"/>
                <circle cx={tX(1)} cy={tY(0.5)} r={5} fill="#fff" stroke="#c0392b" strokeWidth="2"/>
                <line x1={tX(1.07)} y1={tY(1.5)} x2={tX(2)} y2={tY(1.5)} stroke="#c0392b" strokeWidth="2.5"/>
                <circle cx={tX(1)} cy={tY(1.5)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
                <line x1={tX(1)+10} y1={tY(0.5)} x2={tX(1)+10} y2={tY(1.5)} stroke="#e07b00" strokeWidth="1.8"/>
                <text x={tX(1)+14} y={(tY(0.5)+tY(1.5))/2+4} fontSize="9" fill="#e07b00" fontWeight="700">jump</text>
              </g>
            ),
          },
          {
            title: 'Infinite',
            subtitle: '(vertical asymptote)',
            color: '#27ae60', fill: '#f0fff4',
            desc: 'f(x) → ±∞ as x → a.\nNeither one-sided limit\nis finite.',
            draw: ({ tX, tY }) => (
              <g>
                {(() => {
                  const pts1=[], pts2=[];
                  for (let x=-1; x<0.88; x+=0.05) { const y=0.5/(x-1)+1; if(y>-1&&y<3.5) pts1.push(`${tX(x).toFixed(1)},${tY(y).toFixed(1)}`); }
                  for (let x=1.12; x<=2.2; x+=0.05) { const y=0.5/(x-1)+1; if(y>-1&&y<3.5) pts2.push(`${tX(x).toFixed(1)},${tY(y).toFixed(1)}`); }
                  return (
                    <g>
                      <line x1={tX(1)} y1={tY(-0.5)} x2={tX(1)} y2={tY(3.3)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="5,3"/>
                      <polyline points={pts1.join(' ')} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
                      <polyline points={pts2.join(' ')} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
                      <text x={tX(1)+4} y={tY(3.2)} fontSize="9" fill="#27ae60">x=a</text>
                    </g>
                  );
                })()}
              </g>
            ),
          },
          {
            title: 'Oscillating',
            subtitle: '(essential)',
            color: '#9b59b6', fill: '#f5d0ff',
            desc: 'Limit does not exist because\nthe function oscillates\ninfinitely fast near x = a.',
            draw: ({ tX, tY }) => (
              <g>
                {(() => {
                  const pts = [];
                  for (let x = 0.08; x <= 2; x += 0.012) {
                    const y = 1 + Math.sin(6 / x) * (0.6 * (1 - x/3));
                    if (y > -0.5 && y < 3.5) pts.push(`${tX(x).toFixed(1)},${tY(y).toFixed(1)}`);
                  }
                  return <polyline points={pts.join(' ')} fill="none" stroke="#9b59b6" strokeWidth="2"/>;
                })()}
                <text x={tX(0.05)} y={tY(1)+4} fontSize="9" fill="#9b59b6">oscillates</text>
              </g>
            ),
          },
        ].map(({ title, subtitle, color, fill, desc, draw }, i) => {
          const px = 14 + i * (panW + 7);
          const oy2 = 210, sc2 = 44;
          const tX = x => px + 28 + x * sc2;
          const tY = y => oy2 - y * sc2;
          return (
            <g key={title}>
              <rect x={px} y={60} width={panW} height={H-70} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={64} width={panW-8} height={26} rx={3} fill={color}/>
              <text x={px+panW/2} y={77} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={88} textAnchor="middle" fontSize="10" fill="#fff">{subtitle}</text>

              {/* Mini graph */}
              <rect x={px+6} y={96} width={panW-12} height={120} rx={3} fill="#fff" stroke={color} strokeWidth="1"/>
              {/* Axes */}
              <line x1={px+14} y1={oy2} x2={px+panW-10} y2={oy2} stroke="#ddd" strokeWidth="1"/>
              <line x1={tX(0)} y1={oy2+18} x2={tX(0)} y2={oy2-110} stroke="#ddd" strokeWidth="1"/>
              <text x={tX(1)} y={oy2+14} textAnchor="middle" fontSize="9" fill="#bbb">a</text>
              <line x1={tX(1)} y1={oy2-4} x2={tX(1)} y2={oy2+4} stroke="#bbb" strokeWidth="1"/>
              {draw({ tX, tY })}

              {/* Description */}
              {desc.split('\n').map((ln, j) => (
                <text key={j} x={px+panW/2} y={228+j*16} textAnchor="middle" fontSize="10" fill="#444">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Continuity requires three things to hold at once: the value must exist, the limit must exist, and they must be equal. Removable discontinuities (holes) have a limit but a missing or mismatched value — they can be patched. Jump discontinuities have two finite limits that disagree. Infinite and oscillating discontinuities are more severe and cannot be repaired by redefining a single point.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Limits at Infinity and Asymptotes
// ─────────────────────────────────────────────────────────────────
function DiagramLimitsAtInfinity() {
  const W = 900, H = 340;

  // f(x) = (2x²+1)/(x²+3)  →  horizontal asymptote y=2
  // g(x) = 1/x  →  horizontal y=0, vertical x=0
  // Also show oblique asymptote: h(x) = (x²+x+1)/(x+1) = x + 1/(x+1) → oblique y=x

  const f  = x => (2*x*x+1)/(x*x+3);
  const g  = x => 1/x;
  const ob = x => (x*x+x+1)/(x+1);   // oblique: approaches x as x→∞

  const ox = 220, oy = 200, scX = 36, scY = 60;
  const toX = x => ox + x * scX;
  const toY = y => oy - y * scY;

  const makePath = (fn, x0, x1, step, skip) => {
    const segs=[], cur=[];
    for (let x=x0; x<=x1; x+=step) {
      const y=fn(x), sy=toY(y);
      if (!isFinite(y)||sy<12||sy>H-12||(skip&&Math.abs(x)<skip)) {
        if (cur.length>1) segs.push('M '+cur.join(' L ')); cur.length=0;
      } else cur.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
    }
    if (cur.length>1) segs.push('M '+cur.join(' L '));
    return segs.join(' ');
  };

  const fPath  = makePath(f, -7, 7, 0.08);
  const gPathL = makePath(g, -7, -0.14, 0.08);
  const gPathR = makePath(g, 0.14, 7, 0.08);
  const obPath = makePath(ob, -7, -1.15, 0.08);
  const obPath2= makePath(ob, -0.85, 7,  0.08);

  // Right info panel
  const panX = 570, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Limits at Infinity — Horizontal, Vertical, and Oblique Asymptotes</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Graph panel */}
        <rect x={10} y={10} width={550} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={285} y={28} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Three asymptote types on one graph</text>

        {/* Grid */}
        {[-6,-4,-2,0,2,4,6].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={12} x2={toX(v)} y2={H-12} stroke="#f4f4f4" strokeWidth="1"/>
          </g>
        ))}
        {[-2,-1,0,1,2,3].map(v => (
          <line key={v} x1={toX(-7)} y1={toY(v)} x2={toX(7)} y2={toY(v)} stroke="#f4f4f4" strokeWidth="1"/>
        ))}

        {/* Asymptote lines */}
        {/* y=2 horizontal */}
        <line x1={toX(-7)} y1={toY(2)} x2={toX(7)} y2={toY(2)} stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="10,5"/>
        <text x={toX(6.6)} y={toY(2)-7} fontSize="11" fill="#2E75B6" fontWeight="700">y=2</text>
        {/* y=0 horizontal */}
        <line x1={toX(-7)} y1={toY(0)} x2={toX(7)} y2={toY(0)} stroke="#1F3864" strokeWidth="1.5"/>
        {/* x=0 vertical */}
        <line x1={toX(0)} y1={12} x2={toX(0)} y2={H-12} stroke="#c0392b" strokeWidth="1.8" strokeDasharray="8,4"/>
        <text x={toX(0)+5} y={28} fontSize="11" fill="#c0392b" fontWeight="700">x=0 (VA)</text>
        {/* x=-1 vertical for oblique */}
        <line x1={toX(-1)} y1={12} x2={toX(-1)} y2={H-12} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="5,4"/>
        <text x={toX(-1)+4} y={H-18} fontSize="10" fill="#9b59b6">x=−1 (VA)</text>
        {/* oblique asymptote y=x */}
        <line x1={toX(-5)} y1={toY(-5)} x2={toX(5)} y2={toY(5)} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="10,4"/>
        <text x={toX(4.6)} y={toY(4.6)+18} fontSize="11" fill="#27ae60" fontWeight="700">y=x (OA)</text>

        {/* Axes */}
        <line x1={toX(-7)} y1={oy} x2={toX(7.3)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={H-12} x2={ox} y2={12} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX(7.3)+4} y={oy+4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox+4} y={14} fontSize="12" fill="#1F3864">y</text>

        {[-6,-4,-2,2,4,6].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy-3} x2={toX(v)} y2={oy+3} stroke="#888" strokeWidth="1"/>
            <text x={toX(v)} y={oy+15} textAnchor="middle" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}
        {[-2,-1,1,2,3].map(v => (
          <g key={v}>
            <line x1={ox-3} y1={toY(v)} x2={ox+3} y2={toY(v)} stroke="#888" strokeWidth="1"/>
            <text x={ox-7} y={toY(v)+4} textAnchor="end" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Curves */}
        <path d={fPath}   fill="none" stroke="#2E75B6" strokeWidth="2.8"/>
        <path d={gPathL}  fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <path d={gPathR}  fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <path d={obPath}  fill="none" stroke="#27ae60" strokeWidth="2.3"/>
        <path d={obPath2} fill="none" stroke="#27ae60" strokeWidth="2.3"/>

        {/* Labels */}
        <text x={toX(4.5)}  y={toY(f(4.5))-12}  fontSize="12" fill="#2E75B6" fontWeight="700">f(x)=(2x²+1)/(x²+3)</text>
        <text x={toX(2.8)}  y={toY(g(2.8))+16}   fontSize="12" fill="#c0392b" fontWeight="700">g(x)=1/x</text>
        <text x={toX(3.5)}  y={toY(ob(3.5))+18}  fontSize="12" fill="#27ae60" fontWeight="700">h(x)=(x²+x+1)/(x+1)</text>

        {/* Right panel */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Asymptote Rules</text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6',
            lines:['Horizontal Asymptote', 'y = lim[x→±∞] f(x)', 'For P/Q: compare degrees', 'deg P < deg Q → y=0', 'deg P = deg Q → y = lead.coeff ratio', 'deg P > deg Q → no HA'] },
          { y:160, bg:'#fff0f0', bc:'#c0392b',
            lines:['Vertical Asymptote', 'x = a where denom = 0', 'and numerator ≠ 0', 'lim f(x) = ±∞ as x→a'] },
          { y:244, bg:'#f0fff4', bc:'#27ae60',
            lines:['Oblique Asymptote', 'When deg P = deg Q + 1', 'Do polynomial long division:', 'y = quotient (ignore remainder)'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={78} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            {lines.map((ln, j) => (
              <text key={j} x={panX+16} y={y+16+j*13} fontSize="11"
                fill={j===0 ? bc : j<=2 ? '#1F3864' : '#555'}
                fontWeight={j===0 ? '700' : '400'}
                fontFamily={j===1 ? "'Courier New',monospace" : "'Georgia',serif"}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Asymptotes describe long-run and blow-up behaviour. Horizontal asymptotes come from limits at ±∞ — for rational functions the degree comparison gives the answer instantly. Vertical asymptotes occur where the denominator vanishes (and the numerator doesn't). Oblique asymptotes arise when the numerator's degree exceeds the denominator's by exactly one — use long division.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — The Squeeze Theorem and Special Limits
// ─────────────────────────────────────────────────────────────────
function DiagramSqueezeAndSpecial() {
  const W = 900, H = 330;

  // Squeeze: -x² ≤ x²sin(1/x) ≤ x²  near 0
  const upper = x => x*x;
  const lower = x => -x*x;
  const mid   = x => x === 0 ? 0 : x*x*Math.sin(1/x);

  const ox = 80, oy = 185, scX = 150, scY = 150;
  const toX = x => ox + (x + 1.1) * scX;
  const toY = y => oy - y * scY;

  const midPts = [], upPts = [], loPts = [];
  for (let x = -1.05; x <= 1.05; x += 0.007) {
    const sxp = toX(x), sym = toY(mid(x)), syu = toY(upper(x)), syl = toY(lower(x));
    if (sym > 12 && sym < H-12) midPts.push(`${sxp.toFixed(1)},${sym.toFixed(1)}`);
    if (syu > 12 && syu < H-12) upPts.push(`${sxp.toFixed(1)},${syu.toFixed(1)}`);
    if (syl > 12 && syl < H-12) loPts.push(`${sxp.toFixed(1)},${syl.toFixed(1)}`);
  }

  // Right panel: special limits
  const panX = 460, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Squeeze Theorem and the Most Important Special Limits</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: squeeze graph ── */}
        <rect x={10} y={10} width={440} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={225} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Squeeze Theorem: −x² ≤ x²sin(1/x) ≤ x²  →  limit = 0 at x = 0</text>

        {/* Grid */}
        {[-1,-0.5,0,0.5,1].map(v => (
          <line key={v} x1={toX(v)} y1={12} x2={toX(v)} y2={H-12} stroke="#f4f4f4" strokeWidth="1"/>
        ))}

        {/* Axes */}
        <line x1={toX(-1.1)} y1={oy} x2={toX(1.1)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={toY(1.15)} x2={ox} y2={toY(-1.15)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX(1.1)+4} y={oy+4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox+4} y={toY(1.15)-4} fontSize="12" fill="#1F3864">y</text>

        {[-1,-0.5,0.5,1].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy-3} x2={toX(v)} y2={oy+3} stroke="#888" strokeWidth="1"/>
            <text x={toX(v)} y={oy+15} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}
        {[-1,1].map(v => (
          <g key={v}>
            <line x1={ox-3} y1={toY(v)} x2={ox+3} y2={toY(v)} stroke="#888" strokeWidth="1"/>
            <text x={ox-7} y={toY(v)+4} textAnchor="end" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Shaded band between upper and lower */}
        {(() => {
          const band = [...upPts, ...[...loPts].reverse()];
          return <polygon points={band.join(' ')} fill="#d0e8ff" opacity="0.35"/>;
        })()}

        {/* Upper and lower bounds */}
        <polyline points={upPts.join(' ')} fill="none" stroke="#27ae60" strokeWidth="2.5"/>
        <polyline points={loPts.join(' ')} fill="none" stroke="#27ae60" strokeWidth="2.5"/>

        {/* Squeezed function */}
        <polyline points={midPts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="2"/>

        {/* Labels */}
        <text x={toX(0.7)}  y={toY(upper(0.7))-10}  fontSize="12" fill="#27ae60" fontWeight="700">y = x²</text>
        <text x={toX(0.7)}  y={toY(lower(0.7))+16}  fontSize="12" fill="#27ae60" fontWeight="700">y = −x²</text>
        <text x={toX(-0.72)} y={toY(mid(-0.72))-12} fontSize="12" fill="#2E75B6" fontWeight="700">x²sin(1/x)</text>

        {/* Limit at 0 */}
        <circle cx={toX(0)} cy={toY(0)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={toX(0)+12} y={toY(0)-8} fontSize="12" fill="#c0392b" fontWeight="700">limit = 0</text>

        {/* Squeeze arrows */}
        <text x={toX(0.22)} y={toY(upper(0.22)/2)-4} fontSize="11" fill="#1F3864" fontWeight="700">squeeze</text>

        {/* Theorem box */}
        <rect x={18} y={H-66} width={424} height={50} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={230} y={H-50} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Squeeze Theorem: if g(x) ≤ f(x) ≤ h(x) near a, and</text>
        <text x={230} y={H-34} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">lim g(x) = lim h(x) = L  →  lim f(x) = L</text>
        <text x={230} y={H-20} textAnchor="middle" fontSize="10" fill="#555">The squeezed function is forced to the same limit, even if it oscillates wildly</text>

        {/* ── RIGHT: special limits ── */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Fundamental Special Limits</text>

        {[
          { formula:'lim sin(x)/x = 1',       note:'x→0; proved by squeeze theorem\nFoundation of all trig derivatives', color:'#2E75B6' },
          { formula:'lim (1−cos x)/x = 0',     note:'x→0; follows from sin limit\nUsed in deriving d/dx[cos x]',      color:'#2E75B6' },
          { formula:'lim (1+1/n)ⁿ = e',        note:'n→∞; definition of Euler\'s number\ne ≈ 2.71828…',                color:'#c0392b' },
          { formula:'lim (eˣ−1)/x = 1',        note:'x→0; confirms d/dx[eˣ]=eˣ at 0\nProved via L\'Hôpital or series', color:'#c0392b' },
          { formula:'lim ln(1+x)/x = 1',       note:'x→0; follows from (eˣ−1)/x\nUsed in continuous compounding',    color:'#27ae60' },
          { formula:'lim xⁿ/eˣ = 0  (any n)',  note:'x→∞; exponentials beat powers\nApply L\'Hôpital n times',       color:'#9b59b6' },
          { formula:'lim x^(1/x) = 1',         note:'x→∞; use ln form; power beats log\ne^(ln x / x) → e⁰ = 1',      color:'#e07b00' },
        ].map(({ formula, note, color }, i) => (
          <g key={formula}>
            <rect x={panX+8} y={36+i*40} width={panW-16} height={37} rx={3}
              fill={i%2===0 ? '#f8fbff' : '#fff'} stroke="#eee" strokeWidth="0.5"/>
            <rect x={panX+8} y={36+i*40} width={4} height={37} fill={color} rx={1}/>
            <text x={panX+20} y={36+i*40+15} fontSize="12" fill={color}
              fontFamily="'Courier New',monospace" fontWeight="700">{formula}</text>
            {note.split('\n').map((ln, j) => (
              <text key={j} x={panX+20} y={36+i*40+28+j*11} fontSize="10" fill="#555">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Squeeze Theorem handles limits of functions that oscillate too wildly for direct evaluation — if they are trapped between two functions with the same limit, they must share that limit. The seven special limits are used repeatedly throughout calculus: sin(x)/x at zero underlies all trig differentiation, and (1+1/n)ⁿ at infinity is the very definition of e.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Limits(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Limits</h1>

      <p style={S.p}>
        The limit is the foundational concept of calculus. Before you can define a derivative or an integral, you need to make precise what it means for a function to "approach" a value. A limit describes the behaviour of a function near a point — not at the point itself — and this subtle distinction is what allows calculus to handle holes, jumps, infinite growth, and instantaneous rates of change in a rigorous way.
      </p>

      <InfoBox
        title="Core Idea: A Limit Describes Behaviour Near a Point, Not At It"
        items={[
          "Definition: lim[x→a] f(x) = L means f(x) gets arbitrarily close to L as x approaches a",
          "The value f(a) is irrelevant — the limit only cares about x near a, never x = a itself",
          "Two-sided limit: requires lim⁻ f(x) = lim⁺ f(x) = L (both sides must agree)",
          "Limits are the foundation of derivatives (instantaneous rate), integrals (area), and continuity",
          "ε-δ definition: for every ε > 0, there exists δ > 0 such that |x−a| < δ ⟹ |f(x)−L| < ε",
        ]}
      />

      <h2 style={S.h2}>The Intuitive Idea of a Limit</h2>
      <DiagramLimitIdea />
      <Table2 rows={[
        ["Informal definition",  "lim[x→a] f(x) = L if f(x) can be made as close to L as desired\nby taking x sufficiently close (but not equal) to a"],
        ["ε-δ definition",       "∀ε>0, ∃δ>0 such that 0 < |x−a| < δ  ⟹  |f(x)−L| < ε\nThe formal precision behind 'arbitrarily close'"],
        ["Limit vs. value",      "lim f(x) and f(a) are independent — both, one, or neither may exist\nExample: f(x)=(x²−1)/(x−1) has limit 2 at x=1 but f(1) is undefined"],
        ["Uniqueness",           "A limit, if it exists, is unique\nCannot have two different limits at the same point"],
        ["Notation",             "lim[x→a] f(x) = L   or   f(x) → L as x → a\nLeft-hand: lim⁻; right-hand: lim⁺"],
      ]} />

      <h2 style={S.h2}>One-Sided Limits and Limit Laws</h2>
      <DiagramOneSidedAndLaws />
      <Table2 rows={[
        ["Left-hand limit",      "lim[x→a⁻] f(x): x approaches a from below (x < a)\nOnly considers behaviour on the left side of a"],
        ["Right-hand limit",     "lim[x→a⁺] f(x): x approaches a from above (x > a)\nOnly considers behaviour on the right side of a"],
        ["Two-sided existence",  "lim[x→a] f(x) = L  iff  lim⁻ = lim⁺ = L\nBoth one-sided limits must exist and be equal"],
        ["Sum/product laws",     "lim[f±g] = L±M;  lim[fg] = LM;  lim[f/g] = L/M (M≠0)\nApply when both individual limits exist"],
        ["Direct substitution",  "For polynomials and continuous functions: lim[x→a] f(x) = f(a)\nJust plug in — no calculation needed when f is continuous at a"],
        ["Squeeze (sandwich)",   "g(x) ≤ f(x) ≤ h(x) and lim g = lim h = L  →  lim f = L\nUsed when direct evaluation is impossible due to oscillation"],
      ]} />

      <h2 style={S.h2}>Indeterminate Forms and Techniques</h2>
      <DiagramIndeterminateForms />
      <Table2 rows={[
        ["Indeterminate forms",  "0/0, ∞/∞, 0·∞, ∞−∞, 1^∞, 0⁰, ∞⁰ — require more work\nNot numbers; they signal the limit may or may not exist"],
        ["Factor & cancel",      "For 0/0 with polynomials: factor out the (x−a) causing the zero\nAfter cancellation, substitute x = a directly"],
        ["Conjugate method",     "Multiply by conjugate (a+b)/(a+b) when square roots appear\nUses (√a−b)(√a+b) = a−b² to eliminate the radical"],
        ["L'Hôpital's rule",     "For 0/0 or ∞/∞: lim f/g = lim f'/g' (differentiate top & bottom)\nOnly applies to these two forms; may need to apply repeatedly"],
        ["Dominant terms",       "For x→±∞: only the highest-power terms matter\nExample: (3x²+1)/(2x²−5) → 3/2 as x→∞"],
        ["Exponential forms",    "For 1^∞, 0⁰, ∞⁰: write f = e^(ln f), evaluate lim of exponent\nexponent = lim [g·ln(f)] — often reduces to 0·∞ form"],
      ]} />

      <h2 style={S.h2}>Continuity</h2>
      <DiagramContinuity />
      <Table2 rows={[
        ["Three conditions",     "f continuous at a iff: (1) f(a) defined, (2) lim exists, (3) lim = f(a)\nAll three must hold — failing any one makes f discontinuous at a"],
        ["Removable disc.",      "Limit exists but f(a) is undefined or ≠ limit — a 'hole'\nCan be fixed by redefining f(a) = lim"],
        ["Jump discontinuity",   "lim⁻ ≠ lim⁺ (both finite) — a step in the graph\nCannot be fixed; f is discontinuous no matter how f(a) is defined"],
        ["Infinite disc.",       "f(x) → ±∞ as x → a — vertical asymptote\nNeither one-sided limit is finite"],
        ["Continuous on interval","f continuous on (a,b) if continuous at every point inside\nOn [a,b]: also requires one-sided continuity at the endpoints"],
        ["IVT",                  "If f continuous on [a,b] and f(a) < 0 < f(b), then ∃c∈(a,b) with f(c)=0\nIntermediate Value Theorem — guarantees root existence"],
      ]} />

      <h2 style={S.h2}>Limits at Infinity and Asymptotes</h2>
      <DiagramLimitsAtInfinity />
      <Table2 rows={[
        ["Horizontal asymptote", "y = L if lim[x→±∞] f(x) = L\nFor P/Q: if deg P = deg Q, y = ratio of leading coefficients"],
        ["Vertical asymptote",   "x = a if lim f(x) = ±∞ as x → a\nOccurs where denominator = 0 and numerator ≠ 0"],
        ["Oblique asymptote",    "y = mx+b when deg P = deg Q + 1\nFind by polynomial long division; remainder → 0 as x → ±∞"],
        ["Degree comparison",    "deg P < deg Q  →  lim = 0 (HA at y=0)\ndeg P = deg Q  →  lim = leading coeff. ratio\ndeg P > deg Q  →  no HA (may have OA or grow without bound)"],
        ["Infinite limits",      "lim[x→∞] xⁿ = ∞;  lim[x→∞] 1/xⁿ = 0  (n > 0)\nlim[x→∞] eˣ = ∞;  lim[x→∞] e⁻ˣ = 0"],
        ["Growth hierarchy",     "As x→∞:  ln x ≪ xᵅ ≪ eˣ  (for any α > 0)\nLogarithms grow slowest; exponentials fastest"],
      ]} />

      <h2 style={S.h2}>Squeeze Theorem and Special Limits</h2>
      <DiagramSqueezeAndSpecial />
      <Table2 rows={[
        ["Squeeze theorem",      "If g(x) ≤ f(x) ≤ h(x) near a, and lim g = lim h = L, then lim f = L\nUsed when f oscillates — trap it between two simpler functions"],
        ["sin(x)/x → 1",        "lim[x→0] sin(x)/x = 1  (x in radians!)\nProved geometrically; fundamental to all trig derivatives"],
        ["(1−cos x)/x → 0",     "lim[x→0] (1−cos x)/x = 0\nFollows from sin limit; used in deriving d/dx[cos x]"],
        ["Definition of e",     "lim[n→∞] (1+1/n)ⁿ = e ≈ 2.71828\nAlso: lim[x→0] (1+x)^(1/x) = e — continuous version"],
        ["(eˣ−1)/x → 1",       "lim[x→0] (eˣ−1)/x = 1\nConfirms the derivative of eˣ is eˣ itself"],
        ["Exponential vs power", "lim[x→∞] xⁿ/eˣ = 0 for any fixed n\nExponentials grow faster than any polynomial — apply L'Hôpital n times"],
      ]} />

      <InfoBox
        title="Key Takeaway — Choosing the Right Strategy for a Limit"
        items={[
          "Try direct substitution first — if f is continuous at a, just plug in x = a.",
          "Get 0/0 with a polynomial? → Factor numerator and denominator, cancel (x−a), then substitute.",
          "Get 0/0 with a square root? → Multiply numerator and denominator by the conjugate.",
          "Get 0/0 or ∞/∞ with trig or exp? → L'Hôpital's rule: differentiate top and bottom separately.",
          "Rational function as x → ∞? → Divide every term by the highest power of x in the denominator.",
          "Function oscillates near a? → Try the Squeeze Theorem: find upper and lower bounds with the same limit.",
          "Limit involves eˣ, ln, or a power? → Take logarithm, evaluate the limit of the exponent, then exponentiate.",
          "One-sided limits disagree? → The two-sided limit does not exist — state both one-sided values.",
        ]}
      />
    </div>
  );
}